/**
 * PR 6b — Phase 2 — DTO type-guard helpers for proposal items.
 *
 * Bridges the gap between the discriminated DTO union (`AdminItemDTO` |
 * `ComercialItemDTO`) and call sites that need to render admin-only fields
 * (cost_cents, margin_pct, min_margin_pct) without leaking those fields to
 * comercial users.
 *
 * Why type guards and not casts:
 *   - `(item as AdminItemDTO).cost_cents` is a runtime trap door — the field
 *     might be undefined when the backend strips it for comercial.
 *   - A user-defined type guard (`item is AdminItemDTO`) narrows the union
 *     in the consumer's lexical scope and surfaces the absent-field case
 *     as a compile error instead of `undefined` at runtime.
 *
 * The discriminator is the PRESENCE of `cost_cents` (and the related
 * `margin_pct`). This matches what `stripAdminFields` does in proposalApi.ts:
 * it deletes those keys on the comercial path, so `hasCostCents` agrees with
 * `role === "admin"` for any response that passed through the API client.
 *
 * Defence-in-depth: callers MUST still gate on `response.role === "admin"`
 * BEFORE rendering admin-only columns. The type guard is for narrowing
 * inside `.items.map((item) => …)`, not as a role substitute.
 *
 * See ADR PR 6b, decision C1 ("Type-guard helper module"), and Convenções §4.
 */

import type { AdminItemDTO, ComercialItemDTO } from "../model/Proposal";

// `BaseItemDTO` is intentionally not exported from Proposal.ts — it's the
// internal shared shape behind the discriminated union. We compose the
// union here directly from the two exported leaves.
type AnyItemDTO = AdminItemDTO | ComercialItemDTO;

/** True iff `item` carries the admin-only `cost_cents` field.
 *  Narrows the union to `AdminItemDTO` in the truthy branch. */
export function hasCostCents(item: AnyItemDTO): item is AdminItemDTO {
  // `in` is the safe discriminator — `(item as any).cost_cents !== undefined`
  // would also match a leaked `cost_cents: 0`, which we explicitly DO want
  // to narrow (cost of zero is still admin-only information).
  return (
    "cost_cents" in item &&
    typeof (item as AdminItemDTO).cost_cents === "number"
  );
}

/** True iff `item` carries the admin-only `margin_pct` field.
 *  Narrows the union to `AdminItemDTO` in the truthy branch.
 *
 *  In practice `hasCostCents` and `hasMarginPct` agree — both are stripped
 *  together by `stripAdminFields`. They are exposed separately so a future
 *  partial-leak (e.g. backend leaks `margin_pct` but not `cost_cents`) is
 *  still detectable. */
export function hasMarginPct(item: AnyItemDTO): item is AdminItemDTO {
  return (
    "margin_pct" in item &&
    typeof (item as AdminItemDTO).margin_pct === "number"
  );
}

/** Convenience: extract `cost_cents` only when present, else `null`.
 *  Use this when rendering admin-conditional cells inside a table where
 *  narrowing every call site is noisy. Returns `null` (not `undefined`)
 *  so the `??` operator behaves predictably for downstream formatters. */
export function costCentsOrNull(item: AnyItemDTO): number | null {
  return hasCostCents(item) ? item.cost_cents : null;
}

/** Convenience: extract `margin_pct` only when present, else `null`. */
export function marginPctOrNull(item: AnyItemDTO): number | null {
  return hasMarginPct(item) ? item.margin_pct : null;
}

// Re-export the union alias for ergonomic imports in consumers that mix
// items from admin and comercial responses (e.g. snapshot diff views).
export type { AnyItemDTO };
